// client/src/pages/GiftSubscription.js
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import api from '../services/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { useSettings } from '../hooks/useSettings.js';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Stripe Elements appearance — matches royal/gold theme
const stripeAppearance = {
  theme: 'night',
  variables: {
    colorPrimary: '#D4AF37',
    colorBackground: '#0f0f2e',
    colorText: '#e2e8f0',
    colorTextSecondary: '#8b8bb5',
    colorDanger: '#ef4444',
    fontFamily: '"Lora", serif',
    borderRadius: '10px',
    spacingUnit: '4px',
  },
  rules: {
    '.Input': { border: '1px solid #3b3b6b', backgroundColor: '#0f0f2e', color: '#e2e8f0', padding: '12px' },
    '.Input:focus': { border: '1px solid #D4AF37', boxShadow: '0 0 0 1px #D4AF37' },
    '.Input::placeholder': { color: '#6b6b9b' },
    '.Label': { color: '#a5a5d0', fontSize: '14px' },
    '.Tab': { border: '1px solid #3b3b6b', backgroundColor: '#1a1a3e', color: '#a5a5d0' },
    '.Tab--selected': { border: '1px solid #D4AF37', backgroundColor: '#1a1a3e', color: '#D4AF37' },
    '.Tab:hover': { color: '#D4AF37' },
  },
};

// ─── Helpers ──────────────────────────────────────────────

function renderSalutation(template, recipientName) {
  if (!template) return '';
  const firstName = recipientName ? recipientName.split(' ')[0] : '(first name)';
  return template.replace(/\{name\}/g, firstName);
}

function renderClosing(template, senderName) {
  if (!template) return '';
  return `${template} ${senderName || 'Someone Special'}`;
}

function formatCents(cents) {
  if (typeof cents !== 'number') return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}

const SALUTATIONS = [
  { value: '', label: '(None)' },
  { value: '{name},', label: '(first name),' },
  { value: 'Dear {name},', label: 'Dear (first name),' },
  { value: 'Dearest {name},', label: 'Dearest (first name),' },
  { value: 'Hey {name},', label: 'Hey (first name),' },
  { value: 'Hello {name},', label: 'Hello (first name),' },
  { value: 'Hi {name},', label: 'Hi (first name),' },
];

const CLOSINGS = [
  { value: '', label: '(None)' },
  { value: 'With love,', label: 'With love, (your name)' },
  { value: 'Sincerely,', label: 'Sincerely, (your name)' },
  { value: 'Warmly,', label: 'Warmly, (your name)' },
  { value: 'XOXO,', label: 'XOXO, (your name)' },
  { value: 'Yours truly,', label: 'Yours truly, (your name)' },
  { value: 'Cheers,', label: 'Cheers, (your name)' },
  { value: 'From,', label: 'From, (your name)' },
];

// Shared card class: solid deep blue in light mode, original translucent in dark mode
const cardClass = 'bg-royal-900 dark:bg-royal-800/40 border border-royal-700 dark:border-royal-600';

// ─── Dynamic Progress Steps ──────────────────────────────

function GiftProgressSteps({ steps, currentStepId }) {
  const currentIndex = steps.findIndex((s) => s.id === currentStepId);

  return (
    <div className="flex items-center justify-center mb-8">
      {steps.map((step, index) => {
        const isCompleted = index < currentIndex;
        const isActive = index === currentIndex;

        return (
          <div key={step.id} className="flex items-center">
            <div className="flex flex-col items-center">
              <div
                className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-sm sm:text-base font-bold transition-colors ${
                  isCompleted
                    ? 'bg-gold text-royal-900'
                    : isActive
                    ? 'bg-gold/20 border-2 border-gold text-gold'
                    : 'bg-royal-900 dark:bg-royal-800 border border-royal-700 dark:border-royal-600 text-royal-100 dark:text-royal-200'
                }`}
              >
                {isCompleted ? (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  index + 1
                )}
              </div>
              <span
                className={`mt-1 sm:mt-2 text-xs sm:text-sm font-medium whitespace-nowrap ${
                  isActive ? 'text-gold-dark dark:text-gold' : isCompleted ? 'text-gold-dark dark:text-gold-light' : 'text-royal-500 dark:text-royal-200'
                }`}
              >
                {step.label}
              </span>
            </div>
            {index < steps.length - 1 && (
              <div
                className={`h-0.5 w-6 sm:w-10 md:w-16 mx-1 sm:mx-2 mb-4 sm:mb-6 transition-colors ${
                  index < currentIndex ? 'bg-gold' : 'bg-royal-400 dark:bg-royal-700'
                }`}
              />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ─── Step: Choose Plan ───────────────────────────────────

function ChoosePlanStep({ giftInfo, onSelect }) {
  const intervals = giftInfo?.intervals || ['yearly'];
  const [selectedInterval, setSelectedInterval] = useState(intervals.includes('yearly') ? 'yearly' : intervals[0]);

  if (!giftInfo || !giftInfo.tiers?.length) {
    return (
      <div className="text-center py-12">
        <p className="text-royal-500 dark:text-royal-200">Gift subscriptions are not available at this time.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-display text-2xl text-gold-dark dark:text-gold mb-2">Choose a Plan to Gift</h2>
        <p className="text-royal-500 dark:text-royal-200 text-sm">Pick a subscription for someone special. They can redeem and start it whenever they're ready.</p>
      </div>

      {intervals.length > 1 && (
        <div className="flex justify-center">
          <div className="inline-flex rounded-full border border-royal-600 p-1 bg-royal-800">
            {intervals.includes('monthly') && (
              <button
                onClick={() => setSelectedInterval('monthly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedInterval === 'monthly' ? 'bg-gold text-royal-900' : 'text-white hover:text-gold'
                }`}
              >
                Monthly
              </button>
            )}
            {intervals.includes('yearly') && (
              <button
                onClick={() => setSelectedInterval('yearly')}
                className={`px-5 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedInterval === 'yearly' ? 'bg-gold text-royal-900' : 'text-white hover:text-gold'
                }`}
              >
                Yearly
              </button>
            )}
          </div>
        </div>
      )}

      <div className={`p-3 ${cardClass} rounded-lg text-center`}>
        <p className="text-royal-100 dark:text-royal-200 text-sm">
          Gift purchases are non-refundable. The recipient will have {giftInfo.codeExpiryDays} days to redeem their gift and start their subscription.
        </p>
      </div>

      <div className={`grid gap-4 mx-auto ${
        giftInfo.tiers.length <= 2 ? 'grid-cols-1 sm:grid-cols-2 max-w-2xl' : 'grid-cols-1 md:grid-cols-3 max-w-5xl'
      }`}>
        {giftInfo.tiers.map((tier) => {
          const price = selectedInterval === 'yearly'
            ? parseFloat(tier.priceyearly) || 0
            : parseFloat(tier.pricemonthly) || 0;
          const duration = selectedInterval === 'yearly' ? '1 year' : '1 month';

          return (
            <div
              key={tier.usertypeid}
              className={`rounded-xl border p-5 transition-all cursor-pointer hover:border-gold ${
                tier.ispopular
                  ? 'border-gold/50 bg-royal-900 dark:bg-royal-800/80'
                  : 'border-royal-700 dark:border-royal-600 bg-royal-900 dark:bg-royal-800/40'
              }`}
              onClick={() => onSelect(tier, selectedInterval, price)}
            >
              {tier.ispopular && (
                <span className="text-sm font-bold text-royal-900 bg-gold-gradient px-2 py-0.5 rounded-full">
                  Most Popular
                </span>
              )}
              <h3 className="font-display text-lg text-gold mt-2">{tier.typename}</h3>
              <p className="text-royal-100 dark:text-royal-200 text-sm mb-3">{tier.tagline}</p>

              <div className="mb-4">
                <span className="text-2xl font-bold text-white">${price.toFixed(2)}</span>
                <span className="text-royal-100 dark:text-royal-200 text-sm"> for {duration}</span>
              </div>

              <ul className="space-y-1.5 mb-5">
                {(tier.features || []).slice(0, 5).map((f, i) => (
                  <li key={i} className="text-sm text-white flex items-start gap-1.5">
                    <span className="text-gold mt-0.5 shrink-0">&#10003;</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button className="w-full py-2.5 rounded-lg font-medium text-base transition-colors bg-gold text-royal-900 hover:bg-gold-light">
                Gift {tier.typename}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Step: Billing Address (sub-stage of Payment) ────────
//
// Collects the buyer's billing address before the PaymentElement is
// mounted. Stripe Tax requires the address up-front so it can compute
// sales tax (Utah nexus, etc) and bake it into the PaymentIntent.
// Mirrors the field set used in client/src/components/ui/CheckoutForm.js
// (AddressStep) so the two checkout flows look and validate the same.

function BillingAddressStep({ tier, interval, price, initialAddress, onSubmit, onBack, busy }) {
  const [name, setName] = useState(initialAddress?.name || '');
  const [address, setAddress] = useState(initialAddress?.address || {
    line1: '',
    line2: '',
    city: '',
    state: '',
    postal_code: '',
    country: 'US',
  });
  const [error, setError] = useState(null);

  const duration = interval === 'yearly' ? '1 year' : '1 month';

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    // Same validation rules as the server's validateBillingAddress and
    // the main Checkout's AddressStep, kept in sync deliberately.
    if (!name.trim()) { setError('Please enter your full name.'); return; }
    if (!address.line1.trim()) { setError('Please enter your street address.'); return; }
    if (!address.city.trim()) { setError('Please enter your city.'); return; }
    if (!address.state.trim()) { setError('Please enter your state.'); return; }
    if (!address.postal_code.trim()) { setError('Please enter your ZIP code.'); return; }
    if (address.country === 'US' && !/^\d{5}(-\d{4})?$/.test(address.postal_code.trim())) {
      setError('Please enter a valid 5-digit ZIP code.'); return;
    }

    onSubmit({ name: name.trim(), address });
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-display text-2xl text-gold mb-2">Billing Address</h2>
        <p className="text-royal-200 text-sm">
          We need your billing address to calculate any applicable sales tax.
        </p>
      </div>

      {/* Plan summary */}
      <div className="bg-royal-800/60 border border-royal-600 rounded-xl p-5">
        <div className="flex items-center justify-between mb-2">
          <span className="text-royal-200">Gift</span>
          <span className="text-white font-medium">{tier?.typename} — {duration}</span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-royal-200">Subtotal</span>
          <span className="text-white">${price.toFixed(2)}</span>
        </div>
        <p className="text-royal-200 text-xs mt-3">
          Tax (if any) will be calculated on the next step based on your billing address.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <label className="block text-base font-medium text-royal-100 mb-2">Full name</label>
          <input
            type="text"
            placeholder="Full name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
        </div>
        <div>
          <label className="block text-base font-medium text-royal-100 mb-2">Street address</label>
          <input
            type="text"
            placeholder="Street address"
            value={address.line1}
            onChange={(e) => setAddress({ ...address, line1: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
        </div>
        <div>
          <input
            type="text"
            placeholder="Apt, suite, etc. (optional)"
            value={address.line2}
            onChange={(e) => setAddress({ ...address, line2: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="City"
            value={address.city}
            onChange={(e) => setAddress({ ...address, city: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
          <input
            type="text"
            placeholder="State"
            value={address.state}
            onChange={(e) => setAddress({ ...address, state: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <input
            type="text"
            placeholder="ZIP code"
            value={address.postal_code}
            onChange={(e) => setAddress({ ...address, postal_code: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white placeholder-royal-400 focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          />
          <select
            value={address.country}
            onChange={(e) => setAddress({ ...address, country: e.target.value })}
            className="w-full px-4 py-3 rounded-lg bg-royal-900 border border-royal-600 text-white focus:border-gold focus:ring-1 focus:ring-gold outline-none text-sm"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
            <option value="GB">United Kingdom</option>
            <option value="AU">Australia</option>
            <option value="DE">Germany</option>
            <option value="FR">France</option>
          </select>
        </div>

        {error && (
          <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="flex items-center justify-between pt-2">
          <button type="button" onClick={onBack} className="text-royal-200 hover:text-gold transition-colors text-sm">
            ← Back
          </button>
          <button
            type="submit"
            disabled={busy}
            className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {busy ? (
              <span className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-royal-900 border-t-transparent rounded-full animate-spin" />
                Calculating tax...
              </span>
            ) : 'Continue to Payment'}
          </button>
        </div>

        <p className="text-center text-royal-200 text-sm pt-2 flex items-center justify-center gap-1.5">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
          </svg>
          Secured by Stripe. Your card details never touch our servers.
        </p>
      </form>
    </div>
  );
}

// ─── Step: Payment (sub-stage of Payment, with tax breakdown) ──

function PaymentFormInner({ tier, interval, taxInfo, codeExpiryDays, onSuccess, onBack }) {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState(null);

  const duration = interval === 'yearly' ? '1 year' : '1 month';

  // taxInfo is always provided now (server returns it from /gifts/purchase).
  // Defensive fallback to subtotal-only if for some reason it's missing.
  const subtotal = taxInfo?.subtotal ?? 0;
  const taxAmount = taxInfo?.taxAmount ?? 0;
  const total = taxInfo?.total ?? subtotal;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setProcessing(true);
    setError(null);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setProcessing(false);
      return;
    }

    const { error: confirmError, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (confirmError) {
      setError(confirmError.message);
      setProcessing(false);
      return;
    }

    if (paymentIntent.status === 'succeeded') {
      onSuccess();
    } else {
      setError('Payment was not completed. Please try again.');
      setProcessing(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="font-display text-2xl text-gold mb-2">Payment</h2>
        <p className="text-royal-200 text-sm">
          You are purchasing a {duration} <span className="text-white font-medium">{tier?.typename}</span> subscription as a gift.
        </p>
      </div>

      {/* Order summary with tax breakdown */}
      <div className="bg-royal-800/60 border border-royal-600 rounded-xl p-5">
        <div className="flex justify-between items-center mb-3">
          <span className="text-royal-200">Gift</span>
          <span className="text-white font-medium">{tier?.typename} — {duration}</span>
        </div>

        <div className="border-t border-royal-700 pt-3 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-royal-200">Subtotal</span>
            <span className="text-white">{formatCents(subtotal)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-royal-200">Tax</span>
            <span className="text-white">{formatCents(taxAmount)}</span>
          </div>
          <div className="border-t border-royal-700 pt-2 flex justify-between">
            <span className="text-royal-100 font-medium">Total due today</span>
            <span className="text-white font-bold text-lg">{formatCents(total)}</span>
          </div>
        </div>

        <p className="text-royal-200 text-xs mt-3">
          You will be charged today. The recipient will have {codeExpiryDays} days to redeem. Gift purchases are non-refundable.
        </p>
      </div>

      <form onSubmit={handleSubmit}>
        <PaymentElement />
        {error && (
          <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">{error}</div>
        )}
        <div className="flex items-center justify-between mt-6">
          <button type="button" onClick={onBack} className="text-royal-200 hover:text-gold transition-colors text-sm">← Back</button>
          <button
            type="submit"
            disabled={!stripe || processing}
            className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
          >
            {processing ? 'Processing...' : `Pay ${formatCents(total)}`}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentStep({ clientSecret, tier, interval, taxInfo, codeExpiryDays, onSuccess, onBack }) {
  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-6 h-6 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
        <span className="ml-3 text-royal-200 text-sm">Preparing payment...</span>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: stripeAppearance }}>
      <PaymentFormInner
        tier={tier}
        interval={interval}
        taxInfo={taxInfo}
        codeExpiryDays={codeExpiryDays}
        onSuccess={onSuccess}
        onBack={onBack}
      />
    </Elements>
  );
}

// ─── Step: Send Gift ─────────────────────────────────────

function SendGiftStep({ senderName, siteName, giftcode, codeExpiryDays,
  giftemailhtml, planName, durationLabel, codeExpiresAt,
  onSend, onBack, sending, error: sendError }) {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [emailSalutation, setEmailSalutation] = useState('Dear {name},');
  const [emailClosing, setEmailClosing] = useState('With love,');
  const [emailParagraph, setEmailParagraph] = useState('');
  const [subTab, setSubTab] = useState('recipient');

  const previewRecipient = recipientName ? recipientName.split(' ')[0] : '(first name)';
  const canSend = recipientEmail.trim() && recipientEmail.includes('@');

  // Build live email preview with placeholder replacement
  const buildEmailPreview = () => {
    const salutationRendered = renderSalutation(emailSalutation, previewRecipient);
    const closingRendered = renderClosing(emailClosing, senderName || 'Someone Special');
    const messageRendered = (emailParagraph || '').replace(/\n/g, '<br>');

    let expiryText = '';
    if (codeExpiresAt) {
      expiryText = new Date(codeExpiresAt).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    } else if (codeExpiryDays) {
      const d = new Date();
      d.setDate(d.getDate() + codeExpiryDays);
      expiryText = d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }

    if (giftemailhtml) {
      let html = giftemailhtml;
      html = html.replace(/\{\{site_name\}\}/g, siteName || 'Fairytale');
      html = html.replace(/\{\{recipient_name\}\}/g, recipientName || '(recipient)');
      html = html.replace(/\{\{sender_name\}\}/g, senderName || 'Someone Special');
      html = html.replace(/\{\{salutation\}\}/g, salutationRendered);
      html = html.replace(/\{\{closing\}\}/g, closingRendered);
      html = html.replace(/\{\{personal_message\}\}/g, messageRendered);
      html = html.replace(/\{\{redeem_url\}\}/g, '#');
      html = html.replace(/\{\{gift_code\}\}/g, giftcode || 'gift-xxxxxxxx');
      html = html.replace(/\{\{plan_name\}\}/g, planName || 'Premium');
      html = html.replace(/\{\{duration_label\}\}/g, durationLabel || '1 year');
      html = html.replace(/\{\{code_expires_date\}\}/g, expiryText);
      return html;
    }

    // Fallback: hardcoded preview
    return `
      <table width="100%" cellpadding="0" cellspacing="0" style="max-width:600px;margin:0 auto;font-family:Georgia,serif;">
        <tr>
          <td style="background:#1a237e;padding:30px;text-align:center;">
            <h1 style="color:#d4af37;margin:0;font-size:28px;">${siteName}</h1>
          </td>
        </tr>
        <tr>
          <td style="padding:40px 30px;background:#ffffff;">
            ${salutationRendered ? `<p style="font-size:16px;color:#333;margin:0 0 16px 0;text-align:left;">${salutationRendered}</p>` : ''}
            ${messageRendered ? `<p style="font-size:15px;color:#555;margin:0 0 24px 0;text-align:left;line-height:1.6;">${messageRendered}</p>` : ''}
            <div style="background:#faf6eb;border:1px solid #e8dfc0;border-radius:12px;padding:24px;margin:0 0 24px 0;text-align:center;">
              <p style="font-size:24px;margin:0 0 8px 0;">&#127873;</p>
              <p style="font-size:18px;color:#1a237e;font-weight:bold;margin:0 0 6px 0;">
                ${senderName || 'Someone Special'} gifted you a subscription!
              </p>
              <p style="font-size:15px;color:#555;margin:0 0 16px 0;">
                You've received <strong>${durationLabel || '1 year'}</strong> of
                <strong style="color:#1a237e;">${planName || 'Premium'}</strong>
              </p>
              <div style="margin:20px 0 16px 0;">
                <span style="display:inline-block;background:#1a237e;color:#d4af37;padding:14px 36px;border-radius:9999px;font-weight:bold;font-size:16px;">
                  Claim Your Gift
                </span>
              </div>
              <p style="font-size:13px;color:#888;margin:0 0 4px 0;">
                Gift code: <strong style="color:#555;letter-spacing:1px;">${giftcode || 'gift-xxxxxxxx'}</strong>
              </p>
              ${expiryText ? `<p style="font-size:13px;color:#888;margin:0;">Valid through ${expiryText}</p>` : ''}
            </div>
            ${closingRendered ? `<p style="font-size:15px;color:#555;font-style:italic;margin:24px 0 0 0;text-align:left;">${closingRendered}</p>` : ''}
          </td>
        </tr>
        <tr>
          <td style="background:#0d1339;padding:20px;text-align:center;">
            <p style="color:#e8eaf6;font-size:12px;margin:0;">Sent with love via ${siteName}</p>
          </td>
        </tr>
      </table>
    `;
  };

  const handleSend = () => {
    onSend({
      recipientemail: recipientEmail.trim(),
      recipientname: recipientName.trim() || null,
      emailsalutation: emailSalutation,
      emailclosing: emailClosing,
      emailparagraph: emailParagraph || null,
    });
  };

  return (
    <div className="space-y-5">
      <div className="text-center">
        <h2 className="font-display text-2xl text-gold mb-2">Send Your Gift</h2>
        <p className="text-royal-200 text-sm">Enter the recipient's details and customize the gift email.</p>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2">
        {['recipient', 'email', 'review'].map((tab) => (
          <button
            key={tab}
            onClick={() => setSubTab(tab)}
            className={`flex-1 py-2 text-sm font-medium rounded-lg transition-colors ${
              subTab === tab
                ? 'bg-gold text-royal-900 shadow-md'
                : 'bg-royal-800/50 text-royal-200 border border-royal-600 hover:border-gold/50 hover:text-gold'
            }`}
          >
            {tab === 'recipient' ? 'Recipient' : tab === 'email' ? 'Email Content' : 'Review'}
          </button>
        ))}
      </div>

      {/* Recipient tab */}
      {subTab === 'recipient' && (
        <div className="space-y-4">
          <div>
            <label className="block text-base text-white mb-1.5">Recipient Email <span className="text-red-400">*</span></label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="friend@email.com"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
            />
          </div>
          <div>
            <label className="block text-base text-white mb-1.5">Recipient Name</label>
            <input
              type="text"
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="Their name (used in the email greeting)"
              className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
            />
          </div>
        </div>
      )}

      {/* Email content tab */}
      {subTab === 'email' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-base text-white mb-1.5">Salutation</label>
              <select
                value={emailSalutation}
                onChange={(e) => setEmailSalutation(e.target.value)}
                className="w-full px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
              >
                {SALUTATIONS.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-base text-white mb-1.5">Closing</label>
              <select
                value={emailClosing}
                onChange={(e) => setEmailClosing(e.target.value)}
                className="w-full px-4 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors"
              >
                {CLOSINGS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-base text-white mb-1.5">Personal Message</label>
            <textarea
              value={emailParagraph}
              onChange={(e) => setEmailParagraph(e.target.value)}
              placeholder="Add a personal note..."
              rows={4}
              className="w-full px-4 py-3 bg-royal-800/50 border border-royal-600 rounded-lg text-white text-base placeholder-royal-200 focus:outline-none focus:border-gold focus:ring-1 focus:ring-gold transition-colors resize-y"
            />
          </div>

          {/* Live email preview */}
          <div>
            <div className="flex items-center gap-3 mb-3">
              <h3 className="text-gold font-semibold text-base">Email Preview</h3>
              <div className="flex-1 h-px bg-royal-700/50"></div>
            </div>
            <p className="text-royal-200 text-xs mb-3 italic">
              This is what the recipient will see in their inbox. Links are disabled in preview.
            </p>
            <div className="rounded-xl border border-royal-600 overflow-hidden shadow-lg shadow-black/20 max-w-lg mx-auto">
              <iframe
                srcDoc={buildEmailPreview()}
                title="Gift Email Preview"
                className="w-full border-0 bg-white"
                style={{ minHeight: '600px', display: 'block' }}
                sandbox="allow-same-origin"
              />
            </div>
          </div>
        </div>
      )}

      {/* Review tab */}
      {subTab === 'review' && (
        <div className="space-y-4">
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-5 space-y-3">
            <div className="flex items-center gap-3 mb-1">
              <h3 className="text-gold font-semibold text-base">Gift Summary</h3>
              <div className="flex-1 h-px bg-royal-700/50"></div>
            </div>
            <div className="space-y-2 text-base">
              <div className="flex justify-between">
                <span className="text-royal-200">To</span>
                <span className="text-white font-medium">{recipientName || recipientEmail || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-royal-200">Email</span>
                <span className="text-white">{recipientEmail || '—'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-royal-200">Plan</span>
                <span className="text-white font-medium">{planName} — {durationLabel}</span>
              </div>
              {giftcode && (
                <div className="flex justify-between">
                  <span className="text-royal-200">Gift Code</span>
                  <span className="text-white font-mono text-sm">{giftcode}</span>
                </div>
              )}
            </div>
          </div>

          {sendError && (
            <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">{sendError}</div>
          )}
        </div>
      )}

      {/* Bottom actions */}
      <div className="flex items-center justify-between pt-2">
        <button onClick={onBack} className="text-royal-200 hover:text-gold transition-colors text-sm">← Back</button>
        <button
          onClick={handleSend}
          disabled={!canSend || sending}
          className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-60"
        >
          {sending ? 'Sending...' : 'Send Gift'}
        </button>
      </div>
    </div>
  );
}

// ─── Confirmation Step ──────────────────────────────────

function ConfirmationStep({ giftcode, planName }) {
  return (
    <div className="text-center py-8 space-y-6">
      <div className="text-5xl">🎉</div>
      <h2 className="font-display text-2xl text-gold-dark dark:text-gold">Gift Sent!</h2>
      <p className="text-royal-600 dark:text-royal-200 text-base max-w-md mx-auto">
        Your <span className="text-royal-900 dark:text-white font-medium">{planName}</span> gift has been sent.
        The recipient will receive an email with instructions on how to claim their subscription.
      </p>

      {giftcode && (
        <div className={`inline-block ${cardClass} rounded-xl px-6 py-4`}>
          <p className="text-royal-100 dark:text-royal-200 text-sm mb-1">Gift Code</p>
          <p className="text-gold font-mono text-lg tracking-wider">{giftcode}</p>
          <p className="text-royal-100 dark:text-royal-200 text-xs mt-2">Save this code in case the recipient needs it.</p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-3 justify-center">
        <Link
          to="/gift/my-gifts"
          className="px-6 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors"
        >
          View My Gifts
        </Link>
        <Link
          to="/"
          className="px-6 py-2.5 border border-royal-300 dark:border-royal-600 text-royal-700 dark:text-royal-200 rounded-lg text-base hover:border-gold/50 hover:text-gold-dark dark:hover:text-gold transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────

export default function GiftSubscription() {
  const { user, loading: authLoading } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [giftInfo, setGiftInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);

  // Top-level flow state — these correspond to the visible progress steps
  const [currentStep, setCurrentStep] = useState('plan'); // 'plan' | 'payment' | 'send' | 'done'
  const [selectedTier, setSelectedTier] = useState(null);
  const [selectedInterval, setSelectedInterval] = useState('yearly');
  const [selectedPrice, setSelectedPrice] = useState(0);

  // Internal sub-stage of the Payment step. The progress bar still
  // reads "Payment" while we're in either of these — same UX as the
  // main /checkout flow which collects address before mounting Elements.
  const [paymentSubStep, setPaymentSubStep] = useState('address'); // 'address' | 'pay'

  // Address the buyer entered. Retained so they can go Back from the
  // PaymentElement step and edit it without losing what they typed.
  const [billingAddress, setBillingAddress] = useState(null);

  // Set when /gifts/purchase succeeds. Carries clientSecret + tax breakdown.
  const [purchaseData, setPurchaseData] = useState(null);
  const [creatingPI, setCreatingPI] = useState(false);

  // Set after /gifts/confirm succeeds (gift record created in DB)
  const [giftData, setGiftData] = useState(null);

  // Gift email template (fetched from admin)
  const [giftemailhtml, setGiftemailhtml] = useState('');

  // Send state
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);

  const isLoggedIn = !!user;
  const siteName = settings?.site_name || 'Fairytale';
  const senderName = user ? `${user.firstname || ''} ${user.lastname || ''}`.trim() : '';

  // ─── Build dynamic steps ────────────────────────────────
  const steps = [];
  if (!isLoggedIn) {
    steps.push({ id: 'signin', label: 'Sign In' });
  }
  steps.push({ id: 'plan', label: 'Choose Plan' });
  steps.push({ id: 'payment', label: 'Payment' });
  steps.push({ id: 'send', label: 'Send Gift' });

  // ─── Fetch gift info on mount ───────────────────────────
  useEffect(() => {
    api.get('/gifts/info', false)
      .then((data) => setGiftInfo(data))
      .catch(() => setGiftInfo({ enabled: false }))
      .finally(() => setLoadingInfo(false));
  }, []);

  // ─── Fetch gift email template for preview ──────────────
  const fetchGiftEmailTemplate = async () => {
    try {
      const data = await api.get('/gifts/admin/email-templates');
      const defaultTemplate = (data.templates || []).find((t) => t.isdefault && t.isactive);
      setGiftemailhtml(defaultTemplate?.htmlcontent || '');
    } catch {
      // Non-critical — will use fallback preview
    }
  };

  // ─── Handle plan selection ──────────────────────────────
  // Just stores the selection and moves to the Payment step's address
  // sub-stage. The actual /gifts/purchase call happens AFTER the buyer
  // enters their billing address.
  const handlePlanSelect = (tier, interval, price) => {
    if (!isLoggedIn) {
      sessionStorage.setItem('pendingGift', JSON.stringify({ tierId: tier.usertypeid, interval }));
      navigate(`/login?redirect=/gift`);
      return;
    }
    setSelectedTier(tier);
    setSelectedInterval(interval);
    setSelectedPrice(price);
    setBillingAddress(null);
    setPurchaseData(null);
    setPaymentSubStep('address');
    setCurrentStep('payment');
  };

  // ─── Submit address → create PaymentIntent with tax ─────
  const handleAddressSubmit = async (address) => {
    if (!selectedTier) return;
    setBillingAddress(address);
    setCreatingPI(true);
    setSendError(null);

    try {
      const data = await api.post('/gifts/purchase', {
        usertypeid: selectedTier.usertypeid,
        interval: selectedInterval,
        billingAddress: address,
      });
      setPurchaseData(data);
      setPaymentSubStep('pay');
    } catch (err) {
      console.error('Gift purchase init error:', err);
      setSendError(err.data?.error || 'Unable to start gift purchase');
      // Stay on the address sub-stage so the user can fix and retry
    } finally {
      setCreatingPI(false);
    }
  };

  // ─── Restore pending gift after login redirect ──────────
  useEffect(() => {
    if (!isLoggedIn || loadingInfo || !giftInfo?.enabled) return;

    const urlParams = new URLSearchParams(window.location.search);
    const resumeGiftId = urlParams.get('resume');

    if (resumeGiftId) {
      handleResumeGift(parseInt(resumeGiftId));
      return;
    }

    const pending = sessionStorage.getItem('pendingGift');
    if (pending) {
      sessionStorage.removeItem('pendingGift');
      try {
        const { tierId, interval } = JSON.parse(pending);
        const tier = (giftInfo.tiers || []).find((t) => t.usertypeid === tierId);
        if (tier) {
          const price = interval === 'yearly' ? parseFloat(tier.priceyearly) : parseFloat(tier.pricemonthly);
          // Just bring them to the Payment step's address sub-stage —
          // they'll enter address, then we create the PaymentIntent.
          setSelectedTier(tier);
          setSelectedInterval(interval);
          setSelectedPrice(price);
          setBillingAddress(null);
          setPurchaseData(null);
          setPaymentSubStep('address');
          setCurrentStep('payment');
        }
      } catch { /* ignore */ }
    }
  }, [isLoggedIn, loadingInfo, giftInfo]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Resume a paid-but-unsent gift ──────────────────────
  const handleResumeGift = async (giftid) => {
    try {
      const data = await api.get(`/gifts/${giftid}`);
      const gift = data.gift;
      if (gift && gift.status === 'pending_send') {
        setGiftData({
          giftid: gift.giftid,
          giftcode: gift.giftcode,
          codeExpiryDays: Math.ceil((new Date(gift.giftcodeexpiresat) - new Date()) / (1000 * 60 * 60 * 24)),
          codeExpiresAt: gift.giftcodeexpiresat,
        });
        const tier = (giftInfo.tiers || []).find((t) => t.usertypeid === gift.usertypeid);
        if (tier) setSelectedTier(tier);
        setSelectedInterval(gift.giftinterval);
        // Skip both payment sub-stages — they've already paid.
        setCurrentStep('send');
        fetchGiftEmailTemplate();
      } else {
        setSendError('This gift cannot be resumed.');
      }
    } catch (err) {
      console.error('Resume gift error:', err);
      setSendError('Unable to load gift. It may have been completed or expired.');
    }
  };

  // ─── Payment success → confirm → go to send step ───────
  const handlePaymentSuccess = async () => {
    try {
      const confirmData = await api.post('/gifts/confirm', {
        paymentIntentId: purchaseData.paymentIntentId,
        usertypeid: purchaseData.usertypeid,
        interval: purchaseData.interval,
      });

      setGiftData({
        giftid: confirmData.giftid,
        giftcode: confirmData.giftcode,
        codeExpiryDays: confirmData.codeExpiryDays || purchaseData.codeExpiryDays,
        codeExpiresAt: confirmData.codeExpiresAt || null,
      });

      setCurrentStep('send');
      fetchGiftEmailTemplate();
    } catch (err) {
      console.error('Gift confirm error:', err);
      setSendError(err.data?.error || 'Payment succeeded but we had trouble creating your gift. Please contact support.');
    }
  };

  // ─── Send the gift email ────────────────────────────────
  const handleSendGift = async (sendData) => {
    setSending(true);
    setSendError(null);

    try {
      await api.post(`/gifts/${giftData.giftid}/send`, {
        recipientname: sendData.recipientname,
        recipientemail: sendData.recipientemail,
        sendername: senderName,
        emailsalutation: sendData.emailsalutation,
        emailclosing: sendData.emailclosing,
        emailparagraph: sendData.emailparagraph,
      });

      setCurrentStep('done');
    } catch (err) {
      setSendError(err.data?.error || 'Unable to send gift');
    } finally {
      setSending(false);
    }
  };

  // ─── Loading ────────────────────────────────────────────
  if (authLoading || loadingInfo) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  // ─── Gift feature disabled ──────────────────────────────
  if (!giftInfo?.enabled) {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark">
        <div className="max-w-lg mx-auto px-4 py-16 text-center">
          <div className="text-4xl mb-4">🎁</div>
          <h1 className="font-display text-2xl text-gold-dark dark:text-gold mb-3">Gift a Subscription</h1>
          <p className="text-royal-500 dark:text-royal-200">Gift subscriptions are not available at this time. Please check back soon!</p>
          <Link to="/" className="text-gold-dark dark:text-gold hover:text-gold dark:hover:text-gold-light transition-colors mt-4 inline-block">← Back to Home</Link>
        </div>
      </div>
    );
  }

  // ─── Not logged in — show plan selection with sign-in prompt ─
  if (!isLoggedIn && currentStep === 'plan') {
    return (
      <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="relative overflow-hidden rounded-2xl mb-8 bg-royal-900">
            <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
            <div className="relative text-center py-10 px-4">
              <h1 className="font-display text-3xl text-gold mb-2">🎁 Gift a Subscription</h1>
              <p className="text-white text-base max-w-xl mx-auto">
                Give someone the gift of sending beautiful, animated eCards with music.
              </p>
            </div>
          </div>

          <GiftProgressSteps steps={steps} currentStepId="signin" />

          <div className="max-w-md mx-auto mb-8">
            <div className={`${cardClass} border-gold/30 rounded-xl p-6 text-center`}>
              <p className="text-gold font-semibold text-base mb-2">Sign in to get started</p>
              <p className="text-royal-100 dark:text-royal-200 text-sm mb-4">
                You'll need a free account to purchase a gift subscription.
              </p>
              <div className="flex gap-3 justify-center">
                <Link to="/login?redirect=/gift" className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors">
                  Log In
                </Link>
                <Link to="/register?redirect=/gift" className="px-5 py-2.5 border border-royal-600 text-royal-200 rounded-lg text-base hover:border-royal-200 hover:text-white transition-colors">
                  Create Free Account
                </Link>
              </div>
            </div>
          </div>

          <ChoosePlanStep giftInfo={giftInfo} onSelect={handlePlanSelect} />
        </div>
      </div>
    );
  }

  // ─── Main flow (logged in) ──────────────────────────────
  return (
    <div className="min-h-screen bg-cream dark:bg-bg-dark transition-colors duration-300">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Hero — only on plan and payment steps */}
        {(currentStep === 'plan' || currentStep === 'payment') && (
          <div className="relative overflow-hidden rounded-2xl mb-8 bg-royal-900">
            <div className="absolute inset-0 bg-hero-gradient opacity-80"></div>
            <div className="absolute inset-0" style={{
              background: 'radial-gradient(ellipse at 30% 20%, rgba(212,175,55,0.08) 0%, transparent 50%)'
            }}></div>
            <div className="relative text-center py-10 px-4">
              <h1 className="font-display text-3xl text-gold mb-2">🎁 Gift a Subscription</h1>
              <p className="text-white text-base max-w-xl mx-auto">
                Give someone the gift of sending beautiful, animated eCards with music.
              </p>
            </div>
          </div>
        )}

        {/* Progress steps — hide on confirmation */}
        {currentStep !== 'done' && (
          <GiftProgressSteps steps={steps} currentStepId={currentStep} />
        )}

        {/* Step content */}
        <div className="max-w-3xl mx-auto">
          {currentStep === 'plan' && (
            <ChoosePlanStep giftInfo={giftInfo} onSelect={handlePlanSelect} />
          )}

          {/* Payment step has TWO sub-stages: address collection, then
              the Stripe PaymentElement. The progress bar reads "Payment"
              for both. */}
          {currentStep === 'payment' && (
            <div className="max-w-lg mx-auto">
              <div className={`${cardClass} rounded-2xl p-6 sm:p-8`}>
                {paymentSubStep === 'address' ? (
                  <>
                    <BillingAddressStep
                      tier={selectedTier}
                      interval={selectedInterval}
                      price={selectedPrice}
                      initialAddress={billingAddress}
                      onSubmit={handleAddressSubmit}
                      onBack={() => { setCurrentStep('plan'); }}
                      busy={creatingPI}
                    />
                    {sendError && (
                      <div className="mt-4 p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
                        {sendError}
                      </div>
                    )}
                  </>
                ) : (
                  <PaymentStep
                    clientSecret={purchaseData?.clientSecret}
                    tier={selectedTier}
                    interval={selectedInterval}
                    taxInfo={purchaseData ? {
                      subtotal: purchaseData.subtotal,
                      taxAmount: purchaseData.taxAmount,
                      total: purchaseData.total,
                      currency: purchaseData.currency,
                    } : null}
                    codeExpiryDays={purchaseData?.codeExpiryDays || giftInfo?.codeExpiryDays || 365}
                    onSuccess={handlePaymentSuccess}
                    onBack={() => {
                      // Back to address sub-stage. We deliberately drop
                      // purchaseData so that submitting address again
                      // creates a fresh PaymentIntent (orphaning the old
                      // one in Stripe — that's fine, unfulfilled PIs
                      // auto-expire after 24 hours).
                      setPurchaseData(null);
                      setPaymentSubStep('address');
                    }}
                  />
                )}
              </div>
            </div>
          )}

          {currentStep === 'send' && (
            <div className={`${cardClass} rounded-xl p-4 sm:p-6`}>
              <SendGiftStep
                senderName={senderName}
                siteName={siteName}
                giftcode={giftData?.giftcode || ''}
                codeExpiryDays={giftData?.codeExpiryDays || 365}
                giftemailhtml={giftemailhtml}
                planName={selectedTier?.typename || purchaseData?.planName || ''}
                durationLabel={selectedInterval === 'yearly' ? '1 year' : '1 month'}
                codeExpiresAt={giftData?.codeExpiresAt || null}
                onSend={handleSendGift}
                onBack={() => setCurrentStep('plan')}
                sending={sending}
                error={sendError}
              />
            </div>
          )}

          {currentStep === 'done' && (
            <ConfirmationStep
              giftcode={giftData?.giftcode || ''}
              planName={selectedTier?.typename || purchaseData?.planName || ''}
            />
          )}
        </div>

        {/* Pending gift notice */}
        {isLoggedIn && currentStep === 'plan' && (
          <PendingGiftsNotice onResume={handleResumeGift} />
        )}
      </div>
    </div>
  );
}

// ─── Pending Gifts Notice ─────────────────────────────────
function PendingGiftsNotice({ onResume }) {
  const [pending, setPending] = useState([]);

  useEffect(() => {
    api.get('/gifts/pending')
      .then((data) => setPending(data.pendingGifts || []))
      .catch(() => {});
  }, []);

  if (pending.length === 0) return null;

  return (
    <div className="mt-8 max-w-2xl mx-auto">
      <div className="p-4 bg-amber-100 dark:bg-amber-900/20 border border-amber-400 dark:border-amber-600/30 rounded-xl">
        <p className="text-amber-800 dark:text-amber-200 font-medium text-sm mb-2">
          You have {pending.length} incomplete gift{pending.length !== 1 ? 's' : ''}
        </p>
        <div className="space-y-2">
          {pending.map((g) => (
            <div key={g.giftid} className="flex items-center justify-between bg-white dark:bg-royal-900/40 rounded-lg px-3 py-2">
              <div>
                <p className="text-royal-900 dark:text-white text-sm">{g.planname} — ${(g.amount / 100).toFixed(2)}</p>
                <p className="text-amber-700 dark:text-royal-200 text-xs">Purchased {new Date(g.createdat).toLocaleDateString()}</p>
              </div>
              <button
                onClick={() => onResume(g.giftid)}
                className="text-amber-800 dark:text-gold text-sm hover:text-amber-900 dark:hover:text-gold-light transition-colors"
              >
                Continue →
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}